import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Flex, Result, Spin, Typography, theme } from '@/shared/antd-imports';
import clientPortalAPI from '@/services/api';
import {
  IClientInput,
  IClientInputsPage,
  IInputSubmission,
  InputsChecklist,
  InputSubmitError,
} from '@/components/inputs/InputsChecklist';

/** Backend origin: the portal API base without its `/api/client-portal` suffix. */
const backendOrigin = (): string =>
  (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/client-portal').replace(/\/api\/client-portal\/?$/, '');

interface IPublicResponse<T> {
  done: boolean;
  body: T & { code?: string };
  message?: string;
}

async function call<T>(path: string, init?: RequestInit): Promise<{ status: number; json: IPublicResponse<T> | null }> {
  const response = await fetch(`${backendOrigin()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });
  let json: IPublicResponse<T> | null = null;
  try {
    json = (await response.json()) as IPublicResponse<T>;
  } catch {
    json = null;
  }
  return { status: response.status, json };
}

/**
 * Public magic-link page `/inputs/:token`: the client completes the checklist without logging in. The token is
 * only sent to the backend (never stored here); files are sent inline with the answer.
 */
const PublicInputsPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const { token: design } = theme.useToken();
  const [page, setPage] = useState<IClientInputsPage | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'error'>('loading');

  const load = useCallback(async () => {
    if (!token) return;
    setState('loading');
    try {
      const { status, json } = await call<IClientInputsPage>(`/public/client-inputs/${encodeURIComponent(token)}`);
      if (status === 404) return setState('invalid');
      if (!json?.done) return setState('error');
      setPage(json.body);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (item: IClientInput, submission: IInputSubmission): Promise<IClientInput> => {
    const fallback = t('inputs.sendError', { defaultValue: 'Could not send this. Please try again.' });
    const files = await Promise.all(
      (submission.files ?? []).map(async file => ({ fileName: file.name, fileType: file.type || 'application/octet-stream', fileData: await clientPortalAPI.readFileAsBase64(file) }))
    );
    let result: { status: number; json: IPublicResponse<{ item: IClientInput }> | null };
    try {
      result = await call<{ item: IClientInput }>(`/public/client-inputs/${encodeURIComponent(token as string)}/items/${item.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({
          value: submission.value,
          secret: submission.secret,
          confirmed: submission.confirmed,
          files: files.length ? files : undefined,
          confirm_not_secret: submission.confirm_not_secret,
        }),
      });
    } catch {
      throw new InputSubmitError(fallback, null);
    }
    if (!result.json?.done || !result.json.body?.item) {
      throw new InputSubmitError(result.json?.message || fallback, result.json?.body?.code ?? null);
    }
    return result.json.body.item;
  };

  return (
    <div style={{ minHeight: '100vh', background: design.colorBgLayout, padding: '24px 16px' }}>
      <Flex vertical gap={16} style={{ maxWidth: 820, margin: '0 auto' }}>
        {state === 'loading' && (
          <Flex justify="center" style={{ padding: 48 }}>
            <Spin size="large" />
          </Flex>
        )}
        {state === 'invalid' && (
          <Result
            status="warning"
            title={t('inputs.linkInvalid', { defaultValue: 'This link is invalid or has expired' })}
            subTitle={t('inputs.linkInvalidHelp', { defaultValue: 'Ask the person who sent it for a new link.' })}
          />
        )}
        {state === 'error' && (
          <Alert
            type="error"
            showIcon
            message={t('inputs.loadError', { defaultValue: 'Could not load the checklist. Please try again.' })}
            action={<Button size="small" onClick={load}>{t('inputs.retry', { defaultValue: 'Try again' })}</Button>}
          />
        )}
        {state === 'ready' && page && (
          <>
            <InputsChecklist page={page} uploadsEnabled={page.uploads_enabled !== false} onSubmit={submit} />
            <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
              {t('inputs.privateLink', { defaultValue: 'This page is private to you. Please do not forward the link.' })}
            </Typography.Text>
          </>
        )}
      </Flex>
    </div>
  );
};

export default PublicInputsPage;
